/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import DtModObservable from '../Game/DtModObservable';
import Game from '../Game/Game';
import RecoveryManager from '../Game/RecoveryManager';
import Currency, { CurrencyType } from '../Localization/Currency';
import LocalizationComponent from '../Localization/LocalizationComponent';
import BetManager from '../Playbar/BetManager';
import IUserMessageManager from '../Playbar/IUserMessageManager';
import IUserMessageNodeBuilder from '../Playbar/IUserMessageNodeBuilder';
import UserMessageManager from '../Playbar/UserMessageManager';
import IReelManager from '../Reel/IReelManager';
import Reel from '../Reel/Reel';
import ReelManager from '../Reel/ReelManager';
import ReelSymbol from '../Reel/Symbol';
import { ISlotGamePlayResult, ISpinData } from '../Server/Object/BaseGamePlayResult';
import { IPlayState } from '../Server/Object/PlayResult';
import PlayResultComponent from '../Server/PlayResultComponent';
import StateComponent from '../StateManager/StateComponent';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import ColorPalletteComponent from '../Utility/Component/ColorPalletteComponent';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import ObservableEvent from '../Utility/Component/Observable/ObservableEvent';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import Decorators from '../Utility/Decorators';
import IPaylineManager from './IPaylineManager';
import { PaylineEvent } from './PaylineEvent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Responsible for payline display and cycling as well as symbol animations.
 */
@cc._decorator.ccclass
export default abstract class PaylineManager extends StateDependencyComponent implements IPaylineManager
{
    // #region Editor Properties

    /** @hidden */
    @property({ type: [StateReference], visible: true })
    protected _cyclePaylineStates: StateReference[] = [new StateReference(StateID.Award)];
    /** On which states should the paylines be cleared. */
    public get CyclePaylineStates(): StateReference[] { return this._cyclePaylineStates; }

    /** @hidden */
    @property({ type: StateReference, visible: true })
    protected _recoverState: StateReference = new StateReference(StateID.Recover);
    /** State used for Recovery, used to skip payline animating while recovering. */
    public get RecoverState(): StateComponent { return this._recoverState.Value; }

    /** @hidden */
    @property({ type: [StateReference], visible: true })
    protected _clearPaylinesOnStates: StateReference[] = [new StateReference(StateID.Request)];
    /** On which states should the paylines be cleared. */
    public get ClearPaylinesStates(): StateReference[] { return this._clearPaylinesOnStates; }

    /** @hidden */
    @property ({ type: cc.Float, visible: true })
    protected _cycleTime: number = 0.5;
    /** How long should a payline be displayed. */
    public get CycleTime(): number { return this._cycleTime; }

    @property ({ type: cc.Float, visible: true })
    protected _minCycles: number = 1;

    /** @hidden */
    @property({ visible: true })
    protected _isBonus: boolean = false;
    /** Is the payline being displayed part of a bonus. */
    public get IsBonus(): boolean { return this._isBonus; }

    @property
    protected excludeBonusPaylines: boolean = false;

    /** @hidden */
    @property({ type: InjectableComponent, visible: true })
    protected _reelManagerRef: InjectableComponent<IReelManager> = new InjectableComponent();
    /** Sets the [[ReelManager]] this manager is running the paylines for. */
    public set ReelManager(value: IReelManager) { this._reelManagerRef.Value = value; }

    // #endregion

    // #region Injectables

    /** @hidden */
    protected betManagerRef: InjectableComponent<BetManager> = new InjectableComponent();
    /** @hidden */
    protected gameRef: InjectableComponent<Game> = new InjectableComponent();
    /** @hidden */
    protected paylineColorsRef: InjectableComponent<ColorPalletteComponent>
        = new InjectableComponent(ReferenceGroup.MainGame, 'Payline');
    /** @hidden */
    protected playResultRef: InjectableComponent<PlayResultComponent> = new InjectableComponent();
    /** @hidden */
    protected userMessageManagerRef: InjectableComponent<IUserMessageManager> = new InjectableComponent();
    /** Sets the [[IUserMessageManager]] for this payline manager to update. */
    public set UserMessageManager(value: IUserMessageManager) { this.userMessageManagerRef.Value = value; }

    /** @hidden */
    protected recoveryManagerRef: InjectableComponent<RecoveryManager> = new InjectableComponent();
    /** @hidden */
    protected localRef: InjectableComponent<LocalizationComponent> = new InjectableComponent();
    /** @hidden */
    protected dtModeRef: InjectableComponent<DtModObservable> = new InjectableComponent();

    // #endregion

    // #region Members

    /** How long has the payline been cycling. */
    protected currentCycleTime: number = 0.0;
    /** Index of the current payline being displayed. */
    protected currentPaylineIndex: number = 0;
    /** Builder object to use for updating the [[UserMessageManager]]. */
    protected defaultViewBuilder: IUserMessageNodeBuilder = null;
    /** Are the paylines currently cycling. */
    protected isCycling: boolean = false;
    /** How many complete payline cycles have been executed. */
    protected paylineCycleCount: number = 0;
    /** The PaylineIds of the active paylines. */
    public get PaylineIds(): number[] { return this.result.paylineID || []; }
    /**
     * An array of each paying payline in a spin. In each array index represents
     * the reel and value represents the row (offset) with 0 being center,
     * positive values descending the reel and negative values ascending.
     */
    protected paylineOffsets: number[][];
    /** An array of current paylines, where the payline is represented by the symbol offsets on each reel. */
    public get PaylineOffsets(): number[][] { return this.paylineOffsets; }

    /** The most recently displayed game play result. */
    protected result: ISlotGamePlayResult = null;
    private _spinData: ISpinData = null;

    private _paylineCount: number = 0;
    private _scalar: number = 0;
    private _denom: number = 0;

    // #endregion

    // #region cc.Component Lifecycle

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this.betManagerRef.SetContext(BetManager);
        this.gameRef.SetContext(Game);
        this.paylineColorsRef.SetContext(ColorPalletteComponent, { InjectOnce: true });
        this.playResultRef.SetContext(PlayResultComponent);
        this._reelManagerRef.SetContext(ReelManager);
        this.userMessageManagerRef.SetContext(UserMessageManager);
        this.recoveryManagerRef.SetContext(RecoveryManager);
        this.localRef.SetContext(LocalizationComponent);
        this.dtModeRef.SetContext(DtModObservable);
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._clearPaylinesOnStates, this._cyclePaylineStates);

        this.localRef.WithValue(local =>
        {
            local.OnChanged(this.updateDefaultViewBuilder);
            this.destructors.push(() => local.OffChanged(this.updateDefaultViewBuilder));
        });

        this.gameRef.WithValue(game =>
        {
            this.onTargetEvent(
                game.AdvancePlayEventDispatcher,
                Game.AdvancePlayEventName,
                this.advancePlayEventHandler);
        });

        this.betManagerRef.WithValue(mgr =>
        {
            mgr.BetObservable.OnChanged(this.betChangedHandler);
            this.destructors.push(() => mgr.BetObservable.OffChanged(this.betChangedHandler));
        });

        this._paylineCount = this._reelManagerRef.WithValue(mgr => mgr.PaylineCount, 0);
    }

    /** @hidden */
    protected firstUpdate(dt: number): void
    {
        super.firstUpdate(dt);
        this.updateDefaultViewBuilder();
    }

    /** @hidden */
    private advancePlayEventHandler = (): void =>
    {
        this.completeCycling(false);
    }

    private completeCycling(clearPaylines: boolean = true): void
    {
        if (!this.isCycling) { return; }
        if (clearPaylines) { this.ClearPaylines(); }
        this._cyclePaylineStates.forEach(stateRef => this.completeState(stateRef.Value), this);
    }

    // Respond to changes in the global game state.
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (this._cyclePaylineStates.some(x => x.Value === event.State)
            || event.State === this._recoverState.Value)
        {
            this.StartCycling(event.State);
        }

        if (this._clearPaylinesOnStates.some(x => x.Value === event.State))
        {
            this.ClearPaylines();
            this.completeState(event.State);
        }
    }

    @Decorators.BindThis()
    private betChangedHandler(event: ObservableEvent<number>): void
    {
        if (!event.RootValueChanged) { return; }
        this.completeCycling();
    }

    // #endregion

    // #region Internal Helpers

    /**
     * Notifies symbols of a PaylineEvent so that they can animate.
     * @param event The PaylineEvent to be emitted.
     * @param offsets Locations on the reel to get the symbols from.
     * @param isScatterPay (Optional) Is this a scatter pay?
     * @param paylineChars (Optional) Payline chars for scatter pay.
     * @returns The maximum animation duration of any of the active symbols.
     */
    protected animatePaylineSymbols(
        event: string,
        offsets: number[],
        isScatterPay: boolean = false,
        paylineChars?: any): number
    {
        let maxSymbolDuration: number = 0;

        offsets.forEach((offset: number, index: number) =>
        {
            if (paylineChars[index] === "?") { return; }

            const symbol: ReelSymbol = this.getSymbolForReelOffset(index, offset);
            if (!symbol || !symbol.SymbolData) { return; }

            maxSymbolDuration = Math.max(maxSymbolDuration, symbol.GetPaylineMaxDuration());
            symbol.node.emit(event);
        }, this);

        return maxSymbolDuration;
    }

    /**
     * Sends PaylineEvents to the reel Symbols associated with the supplied offsets.
     * @param event The PaylineEvent to be emitted to the reel symbols.
     * @param offsets Which symbols on each reel should receive the event.
     * @param isScatterPay (Optional) Is this a scatter pay?
     * @param paylineChars (Optional) Payline chars for a scatter pay.
     */
    protected sendPaylineEvent(
        event: string,
        offsets: number[],
        isScatterPay: boolean = false,
        paylineChars?: any): void
    {
        offsets.forEach((offset: number, index: number) =>
        {
            if (isScatterPay && paylineChars[index] === "?") { return; }

            let symbol: ReelSymbol = this.getSymbolForReelOffset(index, offset);
            if (!symbol) { return; }

            symbol.node.emit(event);
        }, this);
    }

    // Build the display string for the message area on the playbar.
    protected buildPaylineMessage(paylineIndex: number): void
    {
        const paylineId: number = this.result.paylineID[paylineIndex] + 1;
        if (this.excludeBonusPaylines
            && this.result.paylineBonusFlag[paylineIndex] !== 0) { return; }

        let paylineTotalCredits: number = 0;
        this.result.paylinePayout.forEach(payout => paylineTotalCredits += payout);
        const paylineTotalCurrency: Currency = new Currency(this._denom, paylineTotalCredits, CurrencyType.CREDITS);

        const paylinePayout: Currency = new Currency(
            this._denom,
            this.result.paylinePayout[paylineIndex] * this._scalar,
            CurrencyType.CREDITS
        );

        let playState: IPlayState = this.playResultRef.Value.PlayState;

        let bonusWinAmount: Currency = new Currency(this._denom,
            playState.winMc - paylineTotalCurrency.GetValue(), CurrencyType.MC);

        let message: string = this.getPaylineMessage(paylineId, paylinePayout, bonusWinAmount);
        let nodeBuilder: IUserMessageNodeBuilder = this.userMessageManagerRef.Value.GetViewBuilder().AddText(message);

        this.paylineColorsRef.WithValue(colors =>
            {
                nodeBuilder.SetColor(colors.GetColor(paylineIndex));
            });

        nodeBuilder.Build();
    }

    protected getPaylineMessage(paylineId: number, paylinePayout: Currency, bonusWinAmount: Currency): string
    {
        const isScatterPay: boolean = paylineId > this._paylineCount;
        const isBonusPay: boolean = !this._isBonus
            && bonusWinAmount.HasValue()
            && isScatterPay;

        if (isBonusPay)
        {
            return this.localRef.Value.GetStringResource(
                "Payline/BONUS_PAY__0_",
                this.betManagerRef.Value.GetCurrencyDisplay(bonusWinAmount)
            );
        }

        if (isScatterPay)
        {
            if (this._isBonus && !paylinePayout.HasValue())
            {
                if (this._spinData && this._spinData.nRetriggerAward)
                {
                    paylinePayout = new Currency(paylinePayout, this._spinData.nRetriggerAward, CurrencyType.CREDITS);
                } else {
                    CLog.warn('No scatter pay or retrigger amount.', LogTypes.UserMessageManager, this);
                    return "";
                }
            }

            return this.localRef.Value.GetStringResource(
                "Payline/SCATTER_PAYS__0_",
                this.betManagerRef.Value.GetCurrencyDisplay(paylinePayout)
            );
        }

        if (paylinePayout.HasValue())
        {
            return this.localRef.Value.GetStringResource(
                "Payline/LINE__0__PAYS__1_",
                paylineId.toString(),
                this.betManagerRef.Value.GetCurrencyDisplay(paylinePayout)
            );
        }
    }

    // Update the PaylineManager's internal state and advance to the next payline.
    @Decorators.BindThis()
    protected cyclePayline(): void
    {
        if (!this.isCycling) { return; }

        // Clear symbol anims on current payline.
        this.sendPaylineEvent(PaylineEvent.OnClear, this.paylineOffsets[this.currentPaylineIndex]);

        this.currentPaylineIndex++;
        if (this.currentPaylineIndex >= this.paylineOffsets.length)
        {
            this.currentPaylineIndex = 0;
            this.paylineCycleCount++;

            if (this.paylineCycleCount === this._minCycles)
            {
                this.completeCycling(false);
            }
        }

        this.showCurrentPayline();
    }

    /**
     * Finds the matching reel symbol for the given offset and its index.
     * @param offsetIndex The index of this offset in the play result's offset list.
     * @param offset The offset at which we are looking for a symbol.
     */
    private getSymbolForReelOffset(offsetIndex: number, offset: number): ReelSymbol
    {
        const reelIndex: number = this.result.paylineReelIndex[this.currentPaylineIndex][offsetIndex];
        const reel: Reel = this._reelManagerRef.Value.GetReel(reelIndex);
        if (!reel) { return null; }

        return reel.GetSymbolAtOffset(offset);
    }

    /**
     * For each specific type of payline manager to implement how it will show
     * its unique form of paylines.
     */
    protected abstract showCurrentPayline(): void;

    /**
     * Uses the [[IUserMessageBuilder]] to update the user message display in the playbar via [[UserMessageManager]].
     */
    @Decorators.BindThis()
    private updateDefaultViewBuilder(dontBuild: boolean = true): void
    {
        this.userMessageManagerRef.WithValue(userMessageManager =>
            {
                this.defaultViewBuilder = userMessageManager.GetViewBuilder();

                const shouldBuild: boolean = !dontBuild
                    && this.defaultViewBuilder
                    && this.defaultViewBuilder.IsCurrent();

                if (shouldBuild)
                {
                    this.defaultViewBuilder.Build();
                }
            });
    }

    // #endregion

    // #region Public Methods

    /** Removes/stops any actively shown/lit/animating paylines. */
    @Decorators.BindThis()
    public ClearPaylines(): void
    {
        if (!this.isCycling) { return; }
        this.userMessageManagerRef.WithValue(mgr => mgr.Clear());

        if (this.paylineOffsets)
        {
            this.paylineOffsets
                .forEach(paylineOffset => this.sendPaylineEvent.call(this, PaylineEvent.OnClear, paylineOffset), this);
        }

        this.isCycling = false;
    }

    /**
     * Entry point from which the PaylineManager is told to start cycling winning paylines.
     */
    public StartCycling(state: StateComponent): void
    {
        this.updateBetValues();
        if (this.recoveryManagerRef.Value.Recovering)
        {
            this.completeState(state);
            return;
        }
        if (this.isCycling) { return; }

        this.result = this.playResultRef.WithValue(playResult =>
            {
                // Not the bonus PaylineManager and is bonus.
                if (this._isBonus && !playResult.IsBonusSpinActive()) { return; }

                if (this._isBonus)
                {
                    this._spinData = playResult.GetBonusSpinData();
                    return playResult.GetBonusGameResult();
                }

                this._spinData = playResult.GetBaseSpinData();
                return playResult.GetBaseGameResult();
            });

        if (!this.result) { return; }

        this.paylineOffsets = this.result.paylineOffsets;

        this.paylineCycleCount = 0;
        this.currentPaylineIndex = 0;

        if (this.paylineOffsets.length < 1)
        {
            this.ClearPaylines();

            if (!this._isBonus)
            {
                this.updateDefaultViewBuilder(true);
                this.defaultViewBuilder.Build();
            }

            this.completeState(state);
            return;
        }

        this.isCycling = true;
        this.showCurrentPayline();
    }

    /**
     * Reduces cycle time by dt mod.
     */
    protected applyDTModeToCycleTime(): void
    {
        this.dtModeRef.WithValue(dtMod => this.currentCycleTime = dtMod.Reduce(this.currentCycleTime));
    }

    private updateBetValues(): void
    {
        this.betManagerRef.WithValue(mgr =>
            {
                this._denom = mgr.ActiveDenom;
                this._scalar = mgr.CurrentBetScaler;
            }, () => CLog.error('No BetManager configured.', this, LogTypes.Configuration));
    }

    // #endregion
}
